/**
 * ManagementTierCard
 *
 * Compact tier card for management surfaces — Group subscription overview (read-only
 * with a "5 of 12 seats" chip) and Solo subscription view (click-to-select with a
 * "Current" anchor chip). Replaces two near-identical bespoke Card JSX blocks
 * that had drifted into different styling.
 *
 * Marketing-style `TierCard` continues to serve checkout / browse flows — bigger
 * typography, hover-lift, Subscribe button. The two card families are
 * intentionally distinct: marketing for "I'm shopping", management for
 * "I'm administering my plan".
 *
 * Trial state notes: the card itself doesn't change between trial and non-trial
 * (per the prototype). Trial framing happens at the surrounding sidebar +
 * `TrialBanner` layer.
 */

import React from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';

import { useDisplayBundle } from '../bundleDisplay';
import type { BillingInterval, BundleWithStatus } from '../types';
import { formatPrice, getMonthlyAmountCents } from '../utils';

export interface ManagementTierCardProps {
  bundle: BundleWithStatus;
  billingInterval: BillingInterval;
  /**
   * Add-ons whose grants are fully covered by this tier — rendered as
   * "+ JotRx included" lines under the feature stack. Pass the result of
   * `getAddOnsIncludedInTier(tier, addOns)` from the parent.
   */
  includedAddons?: BundleWithStatus[];
  /**
   * Right-side status chip in the header row. Group uses "5 of 12" / "None";
   * Solo typically omits it (selection state is communicated via the
   * `isCurrent` chip and the border treatment).
   */
  statusChip?: { label: string; color?: 'primary' | 'default' };
  /**
   * Drives the card's emphasis:
   *   - `true`: 2px solid primary border + paper bg (in use / selected)
   *   - `false`: 1px solid divider + grey bg (available but not currently active)
   */
  isActive?: boolean;
  /**
   * Anchors a "Current" chip to the user's currently-paid tier. Stays
   * anchored even when a different card is selected (mirrors the prototype's
   * intent: a visual anchor for comparison). Independent of `isActive`.
   */
  isCurrent?: boolean;
  /**
   * When provided, the whole card becomes clickable as a selection target.
   * Group omits this (read-only); Solo wires its `setTier`.
   */
  onSelect?: (bundleId: string) => void;
}

export const ManagementTierCard: React.FC<ManagementTierCardProps> = ({
  bundle: rawBundle,
  billingInterval,
  includedAddons = [],
  statusChip,
  isActive = false,
  isCurrent = false,
  onSelect,
}) => {
  const bundle = useDisplayBundle(rawBundle) as BundleWithStatus;
  const priceCents = getMonthlyAmountCents(bundle, billingInterval === 'year');
  const features = bundle.marketing_features || [];
  const selectable = !!onSelect;

  const handleClick = () => {
    if (onSelect) onSelect(bundle.bundle_id);
  };

  return (
    <Card
      data-testid={`tier-card-${bundle.bundle_id}`}
      onClick={selectable ? handleClick : undefined}
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      onKeyDown={
        selectable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      sx={theme => ({
        position: 'relative',
        height: '100%',
        // MUI Card defaults to 4px radius; bump to 8px so the management
        // cards read as a softer, more "card-y" surface in dense grids.
        borderRadius: 2,
        border: isActive ? '2px solid' : '1px solid',
        borderColor: isActive ? 'primary.main' : 'divider',
        backgroundColor: isActive ? 'background.paper' : 'grey.50',
        cursor: selectable ? 'pointer' : 'default',
        transition: theme.transitions.create(['border-color', 'background-color']),
        '&:hover': selectable ? { borderColor: 'primary.light' } : undefined,
      })}
    >
      {/* "Current" anchor chip — top-right, absolutely positioned so it
          doesn't compete with the in-header status chip. */}
      {isCurrent && (
        <Chip
          label="Current"
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            fontSize: '0.65rem',
            height: 20,
          }}
        />
      )}
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 0.5,
            // Reserve room for the absolute "Current" chip when it's present
            // so the status chip doesn't slide under it.
            pr: isCurrent ? 7 : 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {bundle.name}
          </Typography>
          {statusChip && (
            <Chip
              label={statusChip.label}
              size="small"
              color={statusChip.color ?? 'default'}
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          )}
        </Box>
        {bundle.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontSize: '0.8rem' }}
          >
            {bundle.description}
          </Typography>
        )}
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
          {formatPrice(priceCents, bundle.currency)}
          <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
            {' '}
            /mo
          </Typography>
        </Typography>
        <Stack spacing={0.5}>
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}
            >
              {feature.included ? (
                <CheckCircleIcon
                  sx={{ color: 'success.main', fontSize: '0.95rem', mt: 0.15 }}
                />
              ) : (
                <CancelIcon
                  sx={{ color: 'text.disabled', fontSize: '0.95rem', mt: 0.15 }}
                />
              )}
              <Typography
                variant="caption"
                sx={{
                  flex: 1,
                  color: feature.included ? 'text.primary' : 'text.secondary',
                }}
              >
                {feature.name}
              </Typography>
            </Box>
          ))}
          {includedAddons.length > 0 && (
            <Box sx={{ pt: 0.5 }}>
              {includedAddons.map(addon => (
                <Typography
                  key={addon.bundle_id}
                  variant="caption"
                  sx={{ color: 'success.main', display: 'block' }}
                >
                  + {addon.name} included
                </Typography>
              ))}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
